import Layout from 'components/layout';

import { i18nFunct, useSiteMetadata } from '@lucid/gatsby-theme-lucid';
import { navigate } from '@reach/router';
import { graphql } from 'gatsby';
import debounce from 'lodash.debounce';
import kebabcase from 'lodash.kebabcase';
import {
  Breadcrumb,
  Button,
  Box,
  Heading
} from 'lucid-components-react';
import * as queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { ValueType } from 'react-select';

import StyledSelect from './styled-select';
import Footer from 'components/footer/footer';
import Navbar from 'components/navbar/navbar';
import Grid, { gridGutter, gridLayout } from 'components/grid/grid';
import Link from 'components/link/link';
import TilesGrid from 'components/tiles-grid/tiles-grid';
import Paginator from 'components/paginator/paginator';
import {
  BlogPosts,
  Drupal,
  Drupal_EntityQueryResult,
  Drupal_Node,
  Drupal_NodeCaseStudy,
  Drupal_NodeDiscovery,
  Drupal_NodeEbook,
  Drupal_NodeProductTemplate,
  Drupal_NodeResourceCenter,
  Drupal_NodeVideoLandingPage,
  Drupal_NodeWebinar,
  Drupal_ParagraphTemplateImage,
  Drupal_TaxonomyTerm,
  Drupal_TaxonomyTermResourceCenterPersonas,
  FileConnection
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import { useMenuDefaultFooter, useMenuTopNavigation } from 'hooks/use-menu';
import { TemplateProps, LucidTheme } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getProcessedValue } from 'utils/helpers';
import LucidContainer from 'components/lucid-container/lucid-container';
import { mqMax } from 'theme/base';
import { Text } from 'theme/typography';

interface ResourceCenterDrupal extends Drupal {
  topics: Drupal_EntityQueryResult;
  personas: Drupal_EntityQueryResult;
}

interface ResourceCenterTaxonomyTerm extends Drupal_TaxonomyTerm {
  children: {
    [tid: string]: ResourceCenterTaxonomyTerm;
  };
}

interface ResourceCenterTopics {
  [tid: string]: ResourceCenterTaxonomyTerm;
}

interface OptionType {
  label: string;
  value: number;
}

interface FormattedBlogPost {
  entityBundle: string;
  entityId: string;
  entityUrl: {
    path: string;
  };
  created: string;
  title: string;
  topicIds: string[];
  personaIds: string[];
  fieldImageSrc: string;
  fieldDescription: {
    summary: string;
  }
}

interface ResourceCenterProps extends TemplateProps {
  data: {
    drupal: Drupal;
    i18nData?: FileConnection;
    allBlogPosts: {
      edges: BlogPosts[];
    };
  };
}

const ResourceCenter = ({
  pageContext,
  data,
  location
}: ResourceCenterProps) => {
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;
  const { productUrl } = useSiteMetadata();

  const { topic: queryTopicName, search: querySearch } = queryString.parse(
    location.search
  );
  const i18n = i18nFunct(data.i18nData, i18nKey);

  const collapseTaxonomy = (
    curObj: ResourceCenterTopics,
    term: ResourceCenterTaxonomyTerm
  ) => {
    const { parent, tid } = term;
    const parentTid = parent![0]!.targetId;
    const isRootTopic = parentTid === 0;
    const container = isRootTopic ? curObj : curObj[parentTid!].children;
    container[tid!] = term;
    term.children = {};

    return curObj;
  };

  const drupal = data.drupal as ResourceCenterDrupal;

  const isFirstRun = useRef(true);
  const rawTopics = drupal.topics.entities as ResourceCenterTaxonomyTerm[];
  const topicRefs = {} as any;
  rawTopics
    .map(rawTopic => rawTopic.tid)
    .forEach(
      // eslint-disable-next-line react-hooks/rules-of-hooks
      tid => (topicRefs[tid!] = useRef((null as unknown) as HTMLElement))
    );
  const topics = rawTopics.reduce(collapseTaxonomy, {} as ResourceCenterTopics);
  const topicNameToTid = new Map();
  Object.keys(topics).forEach(tid => {
    const safeName = kebabcase(topics[tid].name!);
    topicNameToTid.set(safeName, tid);
  });

  const rawPersonas = drupal.personas.entities as ResourceCenterTaxonomyTerm[];
  const personas = rawPersonas.reduce(
    collapseTaxonomy,
    {} as ResourceCenterTopics
  );
  const personaNameToTid = new Map();
  Object.keys(personas).forEach(tid => {
    const safeName = kebabcase(personas[tid].name!);
    personaNameToTid.set(safeName, tid);
  });

  const formatBlogPosts = (posts: BlogPosts[]) => {
    return posts.reduce((formattedPosts, post) => {
      const personaIds = post.field_persona?.split(',').map((persona: string) => {
        return personaNameToTid.get(kebabcase(persona));
      });
      const topicIds = post.field_topic?.split(',').map((topic: string) => {
        return topicNameToTid.get(kebabcase(topic));
      });

      formattedPosts.push({
        entityBundle: 'blog_post',
        entityId: `blog-${post.nid}`,
        entityUrl: {
          path: post.path || ''
        },
        created: post.created || '',
        personaIds: personaIds || [],
        topicIds: topicIds || [],
        fieldImageSrc: post.field_featured_image_url || '',
        fieldDescription: {
          summary: post.field_homepage_summary || ''
        },
        title: post.title || ''
      });

      return formattedPosts;
    }, [] as FormattedBlogPost[]);
  };

  const node = drupal.nodeById as Drupal_NodeResourceCenter;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const drupalEntities = drupal.nodeQuery!.entities!.map(
    drupalEntity => drupalEntity!.entityTranslation
  ) as Drupal_Node[];
  const blogPosts = formatBlogPosts(data.allBlogPosts.edges);
  const allEntities = [...drupalEntities, ...blogPosts] as Array<
    Drupal_Node | FormattedBlogPost
  >;
  const sortedEntities = allEntities.sort((a, b): any => {
    return b.created - a.created;
  });

  const [chosenTopic, setChosenTopic] = useState(
    topicNameToTid.get(kebabcase(queryTopicName as string)) || ''
  );

  const [chosenSubtopic, setChosenSubtopic] = useState('');
  const [search, setSearch] = useState((querySearch as string) || '');
  const filterRef = useRef((null as unknown) as HTMLElement);
  const inputRef = useRef((null as unknown) as HTMLInputElement);
  const activePersona = node.fieldRcPersona
    ? node.fieldRcPersona.targetId
    : null;

  const filterByTids = (tids: string[]) => (
    entity: Drupal_Node | FormattedBlogPost
  ) => {
    if (entity && entity.entityBundle === 'discovery') {
      const {
        fieldDiscoveryPersona,
        fieldDiscoveryTopics
      } = entity as Drupal_NodeDiscovery;

      const nodeTids = [
        ...fieldDiscoveryPersona!.map(term => String(term!.targetId)),
        ...fieldDiscoveryTopics!.map(term => String(term!.targetId))
      ];

      return nodeTids.some(tid => tids.includes(tid));
    } else if (entity && entity.entityBundle === 'blog_post') {
      const { topicIds, personaIds } = entity as FormattedBlogPost;
      const nodeIds = [...topicIds, ...personaIds];
      return nodeIds.some(tid => tids.includes(tid));
    } else {
      const { fieldTopic, fieldPersona } = entity as
        | Drupal_NodeEbook
        | Drupal_NodeProductTemplate
        | Drupal_NodeWebinar
        | Drupal_NodeVideoLandingPage
        | Drupal_NodeCaseStudy;
      const nodeTids = [
        ...(fieldTopic || []).map(term => String(term!.targetId)),
        ...(fieldPersona || []).map(term => String(term!.targetId))
      ];

      return nodeTids.some(tid => tids.includes(tid));
    }
  };

  const matchesSearch = (entity: Drupal_Node | FormattedBlogPost) =>
    (entity.title || '').toLowerCase().includes(search.toLowerCase());

  const matchesPersona = (entity: Drupal_Node | FormattedBlogPost) => {
    if (!activePersona) {
      return true;
    }

    if (entity && entity.entityBundle === 'discovery') {
      const { fieldDiscoveryPersona } = entity as Drupal_NodeDiscovery;
      return (
        fieldDiscoveryPersona &&
        fieldDiscoveryPersona.some(
          persona => persona!.targetId === activePersona
        )
      );
    } else if (entity && entity.entityBundle === 'blog_post') {
      const { personaIds } = entity as FormattedBlogPost;
      return personaIds.some(persona => persona === activePersona.toString());
    }

    const { fieldPersona } = entity as
      | Drupal_NodeEbook
      | Drupal_NodeProductTemplate
      | Drupal_NodeWebinar
      | Drupal_NodeVideoLandingPage
      | Drupal_NodeCaseStudy;

    return (
      fieldPersona &&
      fieldPersona.some(persona => persona!.targetId === activePersona)
    );
  };

  const mapEntityToCard = () => ({
    entityId,
    entityBundle,
    title,
    entityUrl,
    ...entity
  }: Drupal_Node | FormattedBlogPost) => {
    let description = '';
    let imgSrc = '';

    if (entityBundle === 'discovery') {
      const {
        fieldFeaturedImage,
        fieldTitleParagraph
      } = entity as Drupal_NodeDiscovery;
      description = getProcessedValue(fieldTitleParagraph);
      imgSrc = fieldFeaturedImage || '';
    }

    if (entityBundle === 'webinar') {
      const {
        fieldImageSrc,
        fieldWebinarDescription
      } = entity as Drupal_NodeWebinar;

      imgSrc = fieldImageSrc || '';
      description = getProcessedValue(fieldWebinarDescription);
    }

    if (entityBundle === 'ebook') {
      const { body, fieldImage } = entity as Drupal_NodeEbook;
      description = getProcessedValue(body);
      imgSrc =
        (fieldImage![0]!.entity as Drupal_ParagraphTemplateImage)
          .fieldImageUrl || '';
    }

    if (entityBundle === 'product_template') {
      const {
        fieldTemplateDescription,
        fieldImage
      } = entity as Drupal_NodeProductTemplate;
      description = getProcessedValue(fieldTemplateDescription);
      imgSrc =
        (fieldImage![0]!.entity as Drupal_ParagraphTemplateImage)
          .fieldImageUrl || '';
    }

    if (entityBundle === 'video_landing_page') {
      const {
        fieldSidebar,
        fieldFirstSectionVideoLink
      } = entity as Drupal_NodeVideoLandingPage;
      description = getProcessedValue(fieldSidebar);
      imgSrc = fieldFirstSectionVideoLink
        ? `https://i.ytimg.com/vi/${fieldFirstSectionVideoLink
          .split('/')
          .pop() as string}/sddefault.jpg`
        : '';
      entityBundle = 'Video';
    }

    if (entityBundle === 'case_study') {
      const { fieldHeroText, fieldHeroImage } = entity as Drupal_NodeCaseStudy;
      description = fieldHeroText || '';
      imgSrc = fieldHeroImage || '';
    }

    if (entityBundle === 'blog_post') {
      const { fieldDescription, fieldImageSrc } = entity as FormattedBlogPost;
      description = fieldDescription.summary || '';
      imgSrc = fieldImageSrc || '';
    }

    const descriptionNoHtml =
      description.replace(/<(?:.|\n)*?>/gm, '').substr(0, 250) + '...';

    return ({
      fieldDescription: {
        summary: descriptionNoHtml
      },
      fieldImageSrc: imgSrc,
      fieldTitle: title,
      fieldSticky: false,
      nid: entityId,
      entityBundle,
      entityUrl
    });
  };

  const heroCards = sortedEntities
    .filter(matchesPersona)
    .slice(0, 3)
    .map(mapEntityToCard());

  let chosenTopics = chosenTopic === '' ? topics : topics[chosenTopic].children;

  if (Object.keys(chosenTopics).length === 0) {
    chosenTopics = { [chosenTopic]: topics[chosenTopic] };
  }

  const cardsByTid = new Map();

  Object.keys(chosenTopics).forEach(tid => {
    const topic = chosenTopics[tid];
    const childKeys = Object.keys(topic.children);
    cardsByTid.set(
      tid,
      sortedEntities
        .filter(matchesPersona)
        .filter(filterByTids([tid, ...childKeys]))
        .filter(matchesSearch)
        .map(mapEntityToCard())
    );
  });

  const tidHasResults = (tid: string) => (cardsByTid.get(tid) || []).length > 0;

  const topicOptions = new Map<string, ValueType<OptionType>>();
  topicOptions.set('', {
    value: '',
    label: i18n('mktg-resource-center-topic-default')
  } as any);
  Object.keys(topics).forEach(tid => {
    topicOptions.set(tid, { value: tid, label: topics[tid]!.name! } as any);
  });

  const updateSearch = (newSearch: string) => {
    setSearch(newSearch);
    inputRef.current.value = newSearch;
    if (newSearch !== '') {
      filterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const debounceSearch = debounce(newSearch => {
    updateSearch(newSearch);
  }, 1000);

  const submitSearch = (evt: React.FormEvent<HTMLFormElement>) => {
    const newSearch = (evt.target as any)[0].value;
    evt.preventDefault();
    debounceSearch.cancel();
    updateSearch(newSearch);
  };

  const hasNoResults = Array.from(cardsByTid.values()).every(
    arr => arr.length === 0
  );

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    const params = [];

    if (chosenTopic && topics[chosenTopic]) {
      const chosenTopicSafeName = kebabcase(topics[chosenTopic].name!);
      params.push(`topic=${chosenTopicSafeName}`);
    }
    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
      inputRef.current.value = search;
    }
    const paramString = params.length > 0 ? `?${params.join('&')}` : '?';

    navigate(paramString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, chosenTopic]);

  // This effect sets the topic when the browser back button is used
  useEffect(() => {
    if ((queryTopicName || '') !== (topics[chosenTopic] || {}).name || '') {
      setChosenTopic(topicNameToTid.get(String(queryTopicName)) || '');
    }
    if ((querySearch || '') !== search) {
      const decodedSearch = decodeURIComponent(String(querySearch || ''));
      setSearch(decodedSearch);
      inputRef.current.value = decodedSearch;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (chosenSubtopic) {
      const chosenRef =
        (topicRefs[chosenSubtopic] || {}).current || filterRef.current;

      chosenRef.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenSubtopic]);

  const navbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  const getMarketoLink = useMarketoLink({
    i18nKey,
    langPrefix
  });

  const { url, copy } = getMarketoLink('1128')!;

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      <Navbar menu={navbarMenu} scrollDistance={1} />
      <Box
        as="main"
        css={(theme: LucidTheme) => ({
          backgroundColor: theme.colors.coolGrey[5]
        })}
      >
        <header
          css={(theme: LucidTheme) => ({
            backgroundColor: theme.colors.coolGrey[100],
            padding: `${theme.space[9]}px ${theme.space[0]}`,
            overflow: 'hidden'
          })}
        >
          {node.fieldImageSrc && (
            <Box
              css={(theme: LucidTheme) => ({
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center top',
                position: 'absolute',
                top: 0,
                right: 0,
                left: 0,
                height: '800px',
                '&::after': {
                  content: '""',
                  boxShadow: `0 0 100px 120px ${theme.colors.coolGrey[90]}`,
                  position: 'absolute',
                  left: 0,
                  bottom: 0,
                  right: 0
                },
                [mqMax.tabletSmall]: {
                  display: 'none'
                }
              })}
              style={{ backgroundImage: `url(${node.fieldImageSrc})` }}
            />
          )}
          <LucidContainer
            css={(theme: LucidTheme) => ({
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              marginBottom: theme.space[10],
              [mqMax.tabletSmall]: {
                marginBottom: theme.space[5]
              }
            })}
          >
            {node.fieldRcPersona && (
              <Breadcrumb
                inverse
                css={(theme: LucidTheme) => ({
                  flex: '0 1 66%',
                  margin: `0 0 ${theme.space[5]}`,
                  color: theme.colors.white,
                  order: 0,
                  [mqMax.tabletSmall]: {
                    flex: '0 1 100%',
                    maxWidth: '100%'
                  }
                })}
                items={[
                  <Link
                    key="/resource-center"
                    inverse
                    css={(theme: LucidTheme) => ({
                      color: theme.colors.white,
                      '&:visited': {
                        color: theme.colors.white
                      },
                      '&:hover': {
                        color: theme.colors.orange[30]
                      },
                      '&:focus': {
                        outline: `2px auto ${theme.colors.orange[30]}`
                      }
                    })}
                    to="/resource-center"
                  >
                    {i18n('mktg-nav-resources')}
                  </Link>,
                  node.fieldRcPersona.entity!.name
                ]}
              />
            )}
            <Box
              css={{
                flex: '0 1 30rem',
                order: 1,
                [mqMax.tabletSmall]: {
                  order: 2,
                  flex: '0 1 100%',
                  maxWidth: '100%'
                }
              }}
            >
              <Heading
                color="white"
                variant="headlineS"
              >
                {node.fieldFirstHeader}
              </Heading>
              <Text
                css={(theme: LucidTheme) => [
                  {
                    color: theme.colors.coolGrey[20]
                  },
                  node.fieldRcPersona && {
                    color: theme.colors.white
                  }
                ]}
                textStyles="subheadXXS"
              >
                {node.fieldHeroText}
              </Text>
            </Box>
            <form
              css={(theme: LucidTheme) => ({
                position: 'relative',
                flex: `0 1 calc(25% - ${theme.space[5]}px)`,
                order: 2,
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  width: '25px',
                  height: '100%',
                  backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSJ3aGl0ZSIgZD0iTTIxLjE3MiAyNGwtNy4zODctNy4zODdjLTEuMzg4Ljg3NC0zLjAyNCAxLjM4Ny00Ljc4NSAxLjM4Ny00Ljk3MSAwLTktNC4wMjktOS05czQuMDI5LTkgOS05IDkgNC4wMjkgOSA5YzAgMS43NjEtLjUxNCAzLjM5OC0xLjM4NyA0Ljc4NWw3LjM4NyA3LjM4Ny0yLjgyOCAyLjgyOHptLTEyLjE3Mi04YzMuODU5IDAgNy0zLjE0IDctN3MtMy4xNDEtNy03LTctNyAzLjE0LTcgNyAzLjE0MSA3IDcgN3oiLz48L3N2Zz4=')",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'top left',
                  backgroundSize: '75%',
                  top: theme.space[2]
                },
                [mqMax.tabletSmall]: {
                  order: 1,
                  flex: '0 1 100%',
                  maxWidth: '100%',
                  margin: `0 0 ${theme.space[8]}px`
                }
              })}
              onSubmit={submitSearch}
            >
              <input
                css={(theme: LucidTheme) => ({
                  width: '100%',
                  height: '2.5rem',
                  background: 'transparent',
                  borderTop: 0,
                  borderRight: 0,
                  borderLeft: 0,
                  borderBottom: `1px solid ${theme.colors.white}`,
                  fontSize: theme.fontSizes[3],
                  fontWeight: theme.fontWeights.bold,
                  color: theme.colors.white,
                  position: 'relative',
                  paddingLeft: theme.space[4]
                })}
                onChange={({ target }) => {
                  const value = target.value;
                  debounceSearch(value);
                }}
                type="search"
              />
            </form>
          </LucidContainer>
          <LucidContainer>
            {!node.fieldRcPersona && (
              <Grid
                css={(theme: LucidTheme) => ({
                  padding: `0 0 ${theme.space[5]}`
                })}
                gutter={gridGutter.large}
                mobileLayout={gridLayout.halves}
                tabletGutter={gridGutter.medium}
                vgutter={gridGutter.small}
              >
                {Object.keys(personas).map(tid => {
                  const persona: Drupal_TaxonomyTermResourceCenterPersonas =
                    personas[tid];
                  return persona.fieldResourceCenterNode! ? (
                    <Link
                      key={tid}
                      inverse
                      css={(theme: LucidTheme) => ({
                        color: theme.colors.white
                      })}
                      to={
                        persona.fieldResourceCenterNode!.entity!.entityUrl!
                          .path!
                      }
                    >
                      {persona.name}
                    </Link>
                  ) : null;
                })}
              </Grid>
            )}
            <TilesGrid currentPage={1} page={1} pageSize={3} pageType={pageType} tiles={heroCards} />
          </LucidContainer>
        </header>
        <section
          ref={filterRef}
          css={(theme: LucidTheme) => ({
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            paddingTop: theme.space[8],
            margin: `${theme.space[5]}px auto ${theme.space[8]}px`,
            zIndex: theme.zIndices[1],
            position: 'relative',
            maxWidth: '90rem',
            width: '100%',
            padding: '0 7.5rem',
            WebkitFontSmoothing: 'antialiased',
            [`@media screen and (max-width: ${theme.breakpoints[5]})`]: {
              paddingLeft: '3.75rem',
              paddingRight: '3.75rem'
            },
            [`@media screen and (max-width: ${theme.breakpoints[2]})`]: {
              paddingLeft: '1.875rem',
              paddingRight: '1.875rem'
            }
          })}
        >
          <Grid
            css={(theme: LucidTheme) => ({
              margin: `0 0 ${theme.space[2]}px`
            })}
            gutter={gridGutter.xlarge}
            layout={gridLayout.halves}
            mobileLayout={gridLayout.whole}
          >
            <StyledSelect
              classNamePrefix="resource-center"
              onChange={(selectedOption: ValueType<OptionType>) => {
                setChosenTopic((selectedOption as any).value);
              }}
              options={Array.from(topicOptions.values()) as any}
              value={topicOptions.get(chosenTopic)}
            />
            <form
              css={(theme: LucidTheme) => ({
                position: 'relative',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  width: '25px',
                  height: '100%',
                  backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjM2Q0NzUyIiBkPSJNMjEuMTcyIDI0bC03LjM4Ny03LjM4N2MtMS4zODguODc0LTMuMDI0IDEuMzg3LTQuNzg1IDEuMzg3LTQuOTcxIDAtOS00LjAyOS05LTlzNC4wMjktOSA5LTkgOSA0LjAyOSA5IDljMCAxLjc2MS0uNTE0IDMuMzk4LTEuMzg3IDQuNzg1bDcuMzg3IDcuMzg3LTIuODI4IDIuODI4em0tMTIuMTcyLThjMy44NTkgMCA3LTMuMTQgNy03cy0zLjE0MS03LTctNy03IDMuMTQtNyA3IDMuMTQxIDcgNyA3eiIvPjwvc3ZnPg==')",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'top left',
                  backgroundSize: '75%',
                  top: theme.space[2]
                }
              })}
              onSubmit={submitSearch}
            >
              <input
                ref={inputRef}
                css={(theme: LucidTheme) => ({
                  width: '100%',
                  height: '2.5rem',
                  background: 'transparent',
                  borderTop: 0,
                  borderRight: 0,
                  borderLeft: 0,
                  borderBottom: `1px solid ${theme.colors.white}`,
                  fontSize: theme.fontSizes[3],
                  fontWeight: theme.fontWeights.bold,
                  color: theme.colors.coolGrey[90],
                  position: 'relative',
                  paddingLeft: theme.space[4],
                  borderBottomColor: theme.colors.coolGrey[90]
                })}
                onChange={({ target }) => {
                  const value = target.value;
                  debounceSearch(value);
                }}
                type="search"
              />
            </form>
          </Grid>
          {Object.keys(chosenTopics).length > 1 && (
            <Grid mobileLayout={gridLayout.halves}>
              {Object.keys(chosenTopics)
                .filter(tidHasResults)
                .map(tid => {
                  const topic = chosenTopics[tid];

                  return (
                    <Button
                      key={tid}
                      asLink
                      css={(theme: LucidTheme) => ({
                        fontSize: theme.fontSizes[3]
                      })}
                      onClick={() =>
                        topicRefs[tid].current.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        })}
                      underline="hover"
                    >
                      {topic.name}
                    </Button>
                  );
                })}
            </Grid>
          )}
        </section>
        {Object.keys(chosenTopics)
          .filter(tidHasResults)
          .map(tid => {
            const topic = chosenTopics[tid];
            const childKeys = Object.keys(topic.children);
            const cards = cardsByTid.get(tid) || [];

            return (
              <section
                key={topic.tid}
                ref={topicRefs[topic.tid!]}
                className="lucid-container"
              >
                <Box
                  css={(theme: LucidTheme) => ({
                    paddingTop: theme.space[8],
                    margin: `-${theme.space[8]}px 0 ${theme.space[9]}px`
                  })}
                >
                  <Heading as="h2" variant="subheadS">
                    {topic.name}
                  </Heading>
                  <Box
                    css={(theme: LucidTheme) => ({
                      margin: `0 0 ${theme.space[6]}px`
                    })}
                    dangerouslySetInnerHTML={{
                      __html: getProcessedValue(topic.description)
                    }}
                  />
                  <Grid
                    css={(theme: LucidTheme) => ({
                      paddingBottom: theme.space[5]
                    })}
                  >
                    {childKeys.map(childTid => (
                      <Button
                        key={childTid}
                        asLink
                        css={(theme: LucidTheme) => ({
                          fontSize: theme.fontSizes[3],
                          padding: 0,
                          margin: 0
                        })}
                        onClick={() => {
                          setChosenTopic(tid);
                          setChosenSubtopic(childTid);
                        }}
                        underline="hover"
                      >
                        {topic.children[childTid].name}
                      </Button>
                    ))}
                  </Grid>
                  <Paginator
                    items={cards}
                    pageSize={3}
                    viewMoreText={i18n('mktg-v-resources-hub-view-more')}
                  >
                    {({ activePage, visibleItems }) => (
                      <TilesGrid currentPage={activePage} pageSize={3} pageType={pageType} tiles={visibleItems} />
                    )}
                  </Paginator>
                </Box>
              </section>
            );
          })}
        {hasNoResults && (
          <Text
            css={(theme: LucidTheme) => ({
              textAlign: 'center',
              margin: `0 0 ${theme.space[11]}px`
            })}
          >
            {i18n('mktg-resource-center-no-results')}
          </Text>
        )}
        <Box
          as="aside"
          css={(theme: LucidTheme) => ({
            background: `center / cover no-repeat ${theme.colors.coolGrey[80]} url('https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/resource-center/personas/team-footer.jpg')`,
            color: theme.colors.white,
            padding: `${theme.space[8]}px 0 ${theme.space[9]}px`,
            [mqMax.tabletSmall]: {
              padding: `${theme.space[5]}px 0`
            }
          })}
        >
          <LucidContainer
            css={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Grid
              gutter={gridGutter.xlarge}
              layout={gridLayout.halves}
              mobileLayout={gridLayout.whole}
            >
              <Box />
              <Box>
                <Heading as="h2" color="white" variant="subheadS">
                  {i18n('mktg-subfooter-header')}
                </Heading>
                <Text
                  css={(theme: LucidTheme) => ({
                    color: theme.colors.white
                  })}
                  textStyles="subheadXXXS"
                >
                  {i18n('mktg_subfooter_p')}
                </Text>
                <Box
                  css={{
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'baseline',
                    justifyContent: 'space-between'
                  }}
                >
                  <Link
                    asButton
                    primary
                    to={`${productUrl}/lucidchart/editNewOrRegister/466e7eac-5163-05d7-b7f1-42440a00189f/`}
                  >
                    {i18n('mktg_subfooter_cta_secondary')}
                  </Link>
                  <Link
                    asButton
                    secondary
                    to={url}
                  >
                    {i18n('mktg_subfooter_cta_primary') || copy}
                  </Link>
                </Box>
              </Box>
            </Grid>
          </LucidContainer>
        </Box>
      </Box>
      <Footer entityTranslations={entityTranslations} menu={footerMenu} />
    </Layout>
  );
};

export default ResourceCenter;

export const query = graphql`
  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $langPrefix: [String!]
    $i18nKey: String!
  ) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeResourceCenter {
          ...entityFields
          fieldFirstHeader
          fieldHeroText
          fieldImageSrc
          fieldRcPersona {
            targetId
            entity {
              name
            }
          }
        }
      }
      nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            {
              field: "type"
              operator: IN
              value: [
                "discovery"
                "webinar"
                "ebook"
                "product_template"
                "video_landing_page"
                "case_study"
              ]
            }
            { field: "status", value: "1", operator: EQUAL }
            { field: "langcode", value: $langPrefix, operator: EQUAL }
          ]
          groups: [
            {
              conjunction: OR
              conditions: [
                { field: "field_is_private", value: "false", operator: EQUAL }
                { field: "field_is_private", operator: IS_NULL }
              ]
            }
            {
              conjunction: OR
              conditions: [
                { field: "field_persona", operator: IS_NOT_NULL }
                { field: "field_topic", operator: IS_NOT_NULL }
                { field: "field_discovery_persona", operator: IS_NOT_NULL }
                { field: "field_discovery_topics", operator: IS_NOT_NULL }
              ]
            }
          ]
        }
        sort: [
          { field: "sticky", direction: DESC }
          { field: "created", direction: DESC }
        ]
      ) {
        entities {
          entityTranslation(language: $LanguageId) {
            entityId
            entityBundle
            entityUrl {
              path
            }
            ... on Drupal_Node {
              title
              sticky
              created
            }
            ... on Drupal_NodeDiscovery {
              fieldTitleParagraph {
                processed
              }
              fieldFeaturedImage
              fieldDiscoveryPersona {
                targetId
              }
              fieldDiscoveryTopics {
                targetId
              }
            }
            ... on Drupal_NodeWebinar {
              fieldWebinarDescription {
                processed
              }
              fieldImageSrc
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeEbook {
              body {
                processed
              }
              fieldImage {
                entity {
                  ... on Drupal_ParagraphTemplateImage {
                    fieldImageUrl
                  }
                }
              }
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeProductTemplate {
              fieldTemplateDescription {
                processed
              }
              fieldImage {
                entity {
                  ... on Drupal_ParagraphTemplateImage {
                    fieldImageUrl
                  }
                }
              }
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeVideoLandingPage {
              fieldSidebar {
                processed
              }
              fieldFirstSectionVideoLink
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
            ... on Drupal_NodeCaseStudy {
              fieldHeroText
              fieldHeroImage
              fieldTopic {
                targetId
              }
              fieldPersona {
                targetId
              }
            }
          }
        }
      }
      topics: taxonomyTermQuery(
        filter: {
          conditions: [
            { field: "vid", value: "resource_center_topics", operator: EQUAL }
          ]
        }
        limit: 10000
      ) {
        entities {
          ... on Drupal_TaxonomyTerm {
            name
            description {
              processed
            }
            tid
            parent {
              targetId
            }
          }
        }
      }
      personas: taxonomyTermQuery(
        filter: {
          conditions: [
            { field: "vid", value: "resource_center_personas", operator: EQUAL }
          ]
        }
        limit: 10000
      ) {
        entities {
          ... on Drupal_TaxonomyTerm {
            name
            tid
            parent {
              targetId
            }
            ... on Drupal_TaxonomyTermResourceCenterPersonas {
              fieldResourceCenterNode {
                entity {
                  entityUrl {
                    path
                  }
                }
              }
            }
          }
        }
      }
    }
    allBlogPosts(
      filter: {
        id: { ne: "dummy" }
        field_persona: { ne: "" }
        field_topic: { ne: "" }
      }
    ) {
      edges {
        node {
          nid
          title
          path
          created
          field_homepage_summary
          field_featured_image_url
          field_persona
          field_topic
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_subfooter_header
            mktg_subfooter_p
            mktg_subfooter_cta_primary
            mktg_subfooter_cta_secondary
            mktg_v_resources_hub_view_more
            mktg_resource_center_no_results
            mktg_resource_center_topic_default
            mktg_nav_resources
          }
        }
      }
    }
  }
`;
